/**
 * Berichten tussen co-editors in dezelfde branch (M4B).
 *
 * Alles staat in het geheugen, hier en in de DurableObject — geen D1, geen
 * R2. Sluit de app en het gesprek is weg. Dat is een keuze, geen
 * tekortkoming: dit is een tikje op de schouder van iemand die op dat moment
 * in hetzelfde model werkt, geen archief dat je later terugzoekt.
 *
 * De store houdt per peer de laatste berichten en of er iets ongelezen is.
 * Hoeveel er ongelezen zijn houden we niet bij — er staan er hooguit vijf,
 * dus een telling zou meer precisie suggereren dan er is.
 */
import { CHAT_HISTORY_LIMIT } from "@ae/shared-types";
import { create } from "zustand";
import type { ChatMessage } from "../realtime/CrdtProvider";

interface ChatState {
  /** Sleutel = e-mail van de peer. */
  messagesByPeer: Record<string, ChatMessage[]>;
  /** Peers waarvan iets binnenkwam dat we nog niet hebben geopend. */
  unreadFrom: string[];
  /** Met wie staat de popup open — null als er geen open staat. */
  openPeer: string | null;
  /**
   * Wat je aan het typen bent, per peer. Staat hier en niet in de component,
   * zodat een remount van het venster je halve zin niet weggooit. De
   * presence-balk waar 'ie in hangt hertekent bij elke verbinding die
   * bij- of afkomt, en dat hoort niet op je invoerveld af te stralen.
   */
  draftByPeer: Record<string, string>;
  setDraft: (peerEmail: string, text: string) => void;
  ingest: (event: { message: ChatMessage; incoming: boolean }, myEmail: string) => void;
  openWith: (peerEmail: string) => void;
  close: () => void;
  reset: () => void;
}

function peerOf(message: ChatMessage, myEmail: string): string {
  return message.senderEmail === myEmail ? message.recipientEmail : message.senderEmail;
}

export const useChatStore = create<ChatState>((set) => ({
  messagesByPeer: {},
  unreadFrom: [],
  openPeer: null,
  draftByPeer: {},

  setDraft: (peerEmail, text) =>
    set((s) => ({ draftByPeer: { ...s.draftByPeer, [peerEmail]: text } })),

  ingest: ({ message, incoming }, myEmail) =>
    set((s) => {
      const peer = peerOf(message, myEmail);
      const existing = s.messagesByPeer[peer] ?? [];
      // De DO echoot naar álle verbindingen van de afzender, dus in twee
      // tabs komt hetzelfde bericht twee keer langs. Op id ontdubbelen.
      if (existing.some((m) => m.id === message.id)) return s;
      const next = [...existing, message].slice(-CHAT_HISTORY_LIMIT);
      // Alleen binnenkomend telt als ongelezen, en niet terwijl je er al
      // naar kijkt.
      const markUnread = incoming && s.openPeer !== peer;
      return {
        messagesByPeer: { ...s.messagesByPeer, [peer]: next },
        unreadFrom:
          markUnread && !s.unreadFrom.includes(peer) ? [...s.unreadFrom, peer] : s.unreadFrom,
      };
    }),

  openWith: (peerEmail) =>
    set((s) => ({
      openPeer: peerEmail,
      unreadFrom: s.unreadFrom.filter((e) => e !== peerEmail),
    })),

  close: () => set({ openPeer: null }),

  // Bij een branchwissel praat je met een ander gezelschap; de gesprekken
  // van daarvoor horen daar niet doorheen te lopen.
  reset: () => set({ messagesByPeer: {}, unreadFrom: [], openPeer: null, draftByPeer: {} }),
}));
